import { Listbox, Transition } from '@headlessui/react';
import { ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { XCircleIcon } from '@heroicons/react/24/outline';
import React, { Fragment, useEffect, useRef, useState } from 'react';
import ReactSelectA from 'react-select';

import { classNames } from '../helpers/functions';

export const Spinner = ({ children, className }) => (
	<div role="status" className="flex items-center py-2">
		<svg
			aria-hidden="true"
			className={classNames(
				'w-4 h-4 text-gray-200 animate-spin dark:text-gray-200 fill-blue-800',
				className,
			)}
			viewBox="0 0 100 101"
			fill="none"
			xmlns="http://www.w3.org/2000/svg"
		>
			<path
				d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
				fill="currentColor"
			/>
			<path
				d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
				fill="currentFill"
			/>
		</svg>
		<span class="sr-only">Saving...</span>
		{children && <span className="text-xs text-gray-500 ml-2">{children}</span>}
	</div>
);

export const Input = React.forwardRef(
	(
		{
			id,
			name,
			type = 'text',
			autoComplete,
			placeholder,
			onChange,
			value,
			key,
			maxLength,
			disabled,
			isEditable,
			isEditing,
			isLoading,
			onClick,
			onBlur,
			onBlurBeta,
			onEnter,
			PreIcon,
			PostIcon,
			onClickPostIcon,
			className,
			showMaxLength,
			...props
		},
		ref,
	) => {
		const reference = ref || useRef();
		const isDefault = !isEditable && !onClick && !onBlur;

		useEffect(() => {
			if (isEditing) {
				reference?.current?.focus();
			}
		}, [isEditing]);

		const onHandleBlur = (e) => {
			const newValue = e.target.value;
			if (typeof onBlur === 'function' && newValue !== value) {
				onBlur(newValue);
				reference?.current?.blur();
			}
			if (typeof onBlurBeta === 'function') {
				onBlurBeta(newValue);
			}
		};

		return isEditing || isDefault ? (
			<>
				<div className="relative flex-1">
					{PreIcon && (
						<div className="pointer-events-none absolute inset-y-0  flex items-center pl-3">
							<PreIcon className="h-5 w-5 text-gray-400" aria-hidden="true" />
						</div>
					)}
					<input
						id={id}
						key={key}
						ref={reference}
						name={name}
						type={type}
						autoComplete={autoComplete}
						className={classNames(
							'placeholder:text-xs block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm',
							disabled ? ' pointer-events-none bg-gray-50 text-gray-500' : '',
							isEditable ? ' pr-10' : '',
							isLoading || PostIcon ? ' pr-10' : '',
							PreIcon ? ' pl-10' : '',
							className,
						)}
						placeholder={placeholder}
						onChange={onChange}
						defaultValue={props.defaultValue || value}
						value={isEditable ? null : value}
						onKeyDown={(e) => {
							if (e.key === 'Enter') {
								onHandleBlur(e);
								if (typeof onEnter === 'function') {
									onEnter(e);
								}
							}
						}}
						onBlur={onHandleBlur}
						maxLength={maxLength}
						{...props}
					/>
					{isLoading ? (
						<div className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-3">
							<Spinner />
						</div>
					) : PostIcon ? (
						<div
							className={classNames(
								'absolute inset-y-0 right-0 flex items-center pr-3',
								onClickPostIcon ? '' : ' pointer-events-none ',
							)}
						>
							<PostIcon
								className={classNames(
									'h-5 w-5 text-gray-400',
									onClickPostIcon ? ' cursor-pointer' : '',
								)}
								aria-hidden="true"
								onClick={() =>
									typeof onClickPostIcon === 'function' ? onClickPostIcon() : {}
								}
							/>
						</div>
					) : null}
				</div>
				{showMaxLength && (
					<div className="text-xxs text-gray-400 mt-1 text-right">{`${
						value?.length || 0
					}/${maxLength} Characters`}</div>
				)}
			</>
		) : (
			<div
				className={classNames(
					'block w-full appearance-none rounded-md py-2 placeholder-gray-400 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm',
					disabled ? ' pointer-events-none bg-gray-50 text-gray-500' : '',
					isEditable ? ' pr-10 px-3 border border-gray-300 shadow-sm' : '',
				)}
				onClick={() => isEditable && onClick()}
			>
				{isEditable && !v ? (
					<div className="text-gray-300">Click to add</div>
				) : (
					v
				)}
			</div>
		);
	},
);

export const Alert = ({ type = 'error', children }) => (
	<div
		className={classNames(
			'border rounded-md py-3 px-5 text-xs mb-3',
			type === 'error' ? 'bg-red-50 border-red-200 text-red-700' : '',
			type === 'success' ? 'bg-green-50 border-green-200 text-green-700' : '',
			type === 'warning'
				? 'bg-orange-50 border-orange-200 text-orange-700'
				: '',
		)}
	>
		{children}
	</div>
);

export const Checkbox = ({ checked, description, onChange, children }) => {
	return (
		<div className="relative flex items-start cursor-pointer">
			<div className="flex h-5 items-center">
				<input
					id="comments"
					aria-describedby="comments-description"
					name="comments"
					type="checkbox"
					className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
					onChange={(e) => onChange(e.target.checked)}
					checked={!!checked}
				/>
			</div>
			<div className="ml-3 text-sm">
				<label htmlFor="comments" className="font-medium text-gray-700">
					{children}
				</label>
				<span id="comments-description" className="text-gray-500">
					<span className="sr-only">{children} </span>
					{description}
				</span>
			</div>
		</div>
	);
};

export const ReactSelect = ({
	value,
	placeholder,
	onChange,
	options = [],
	onClear,
	className,
	CustomOption,
	filterOption,
	disabled,
	formatOptionLabel,
	onMenuOpen,
	blurInputOnSelect,
}) => {
	return (
		<ReactSelectA
			blurInputOnSelect={blurInputOnSelect}
			placeholder={placeholder}
			className={classNames('text-xs', className)}
			classNames={{
				control: (state) => 'rounded-lg !border-gray-200 text-gray-700',
				placeholder: (state) => 'text-xxs !text-gray-700',
				option: ({ isFocused, isSelected, isGroup, isDisabled }) =>
					classNames(isDisabled ? '!text-gray-400' : '!text-gray-700'),
			}}
			options={options}
			onChange={(change, i) => {
				onChange(
					change
						? options
								?.flatMap((o) => o.options || o)
								.filter((o) => !o?.disabled)
								?.find((o) => o?.value === change?.value)
						: null,
				);
			}}
			value={value}
			isOptionDisabled={(option) => option.disabled}
			isClearable={typeof onClear === 'function'}
			components={CustomOption ? { Option: CustomOption } : {}}
			isSearchable={true}
			filterOption={filterOption}
			isDisabled={disabled}
			formatOptionLabel={formatOptionLabel}
			onMenuOpen={onMenuOpen}
		/>
	);
};

export const Select = ({
	value,
	placeholder,
	onChange,
	options = [],
	onClear,
	disabled,
	className,
	border = true,
}) => {
	const Option = (option) => (
		<Listbox.Option
			key={option.value}
			className={({ active }) =>
				classNames(
					active ? 'text-white bg-indigo-600' : 'text-gray-900',
					'relative cursor-default select-none py-2 pl-3 pr-9 ml-4',
					className,
				)
			}
			value={option}
			dis
		>
			{({ selected, active }) => (
				<>
					<span
						className={classNames(
							selected ? 'font-semibold' : 'font-normal',
							'block truncate',
						)}
					>
						{child?.label}
					</span>
				</>
			)}
		</Listbox.Option>
	);

	return (
		<Listbox value={value} onChange={onChange} disabled={disabled}>
			{({ open }) => (
				<>
					<div className="relative mt-1">
						<Listbox.Button
							className={classNames(
								'relative w-full cursor-default rounded-md bg-white py-2 pl-3 pr-10 text-left text-xs sm:text-sm',
								border
									? 'border border-gray-300 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-1 focus:ring-indigo-500'
									: 'text-xxs',
							)}
						>
							<span
								className={classNames(
									'block truncate',
									value ? ' text-gray-900' : 'text-gray-400',
								)}
							>
								{value?.label || placeholder}
							</span>
							<span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
								{typeof onClear === 'function' && value?.label ? (
									<XCircleIcon className="h-5 w-5 text-gray-400" />
								) : (
									<ChevronUpDownIcon
										className="h-5 w-5 text-gray-400"
										aria-hidden="true"
									/>
								)}
							</span>
						</Listbox.Button>

						<Transition
							show={open}
							as={Fragment}
							leave="transition ease-in duration-100"
							leaveFrom="opacity-100"
							leaveTo="opacity-0"
						>
							<Listbox.Options className="absolute z-10 mt-1 max-h-60 w-full overflow-auto rounded-md bg-white py-1 text-base shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none sm:text-sm">
								{options?.map((option) => (
									<Listbox.Option
										key={option.value}
										className={({ active }) =>
											classNames(
												active ? 'text-white bg-indigo-600' : 'text-gray-900',
												'relative cursor-default select-none py-2 pl-3 pr-9',
											)
										}
										value={option}
									>
										{({ selected, active }) => (
											<>
												<span
													className={classNames(
														selected ? 'font-semibold' : 'font-normal',
														'block truncate',
													)}
												>
													{option?.label}
												</span>

												{option?.children?.map((child) => (
													<Option option={child} />
												))}
											</>
										)}
									</Listbox.Option>
								))}
							</Listbox.Options>
						</Transition>
					</div>
				</>
			)}
		</Listbox>
	);
};

export const EditableTextarea = ({
	onClick,
	disabled,
	placeholder,
	isEditing,
	onBlur,
	onChange,
	children,
	isEditable,
	isLoading,
	autoFocus,
	ref,
}) => {
	const textarea = useRef();
	const [height, setHeight] = useState(3);

	const handleOnChange = (e) => {
		const h = e.target.scrollHeight;
		const r = Math.ceil(h / 20) - 1;
		if (r) {
			setHeight(r);
		}
	};
	return isEditing && isEditable ? (
		<>
			<textarea
				placeholder={placeholder}
				ref={textarea}
				//id="about"
				//name="about"
				className={classNames(
					'h-auto whitespace-pre-wrap	block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm',
					disabled ? ' pointer-events-none bg-gray-50 text-gray-500' : '',
				)}
				onBlur={(e) => {
					if (typeof onBlur === 'function') {
						const newValue = e.target.value;
						onBlur(newValue);
					}
				}}
				onFocus={(e) => {
					handleOnChange(e);
					e.currentTarget.setSelectionRange(
						e.currentTarget.value.length,
						e.currentTarget.value.length,
					);
				}}
				onChange={onChange}
				autoFocus={autoFocus}
				height="auto"
				rows={height}
				value={children}
			/>
			{isLoading && <Spinner>Saving...</Spinner>}
		</>
	) : (
		<div
			className="h-auto whitespace-pre-wrap block w-full appearance-none rounded-md border border-gray-300 px-3 py-2 placeholder-gray-400 shadow-sm  sm:text-sm"
			onClick={() => isEditable && onClick()}
		>
			{isEditable && !value ? (
				<div className="text-gray-300">Click to add</div>
			) : (
				v
			)}
		</div>
	);
};
